"use client"

import * as React from "react"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@workspace/ui/components/chart"
import type { ChartConfig } from "@workspace/ui/components/chart"
import { cn } from "@workspace/ui/lib/utils"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// Base Chart Component
// ============================================================================

export interface UIChartProps {
  data: any[]
  config: ChartConfig
  className?: string
  height?: number | string
  showGrid?: boolean
  showAxes?: boolean
  showTooltip?: boolean
  showLegend?: boolean
  id?: string
}

export const UIChart = React.forwardRef<
  HTMLDivElement,
  UIChartProps & { children: React.ReactElement }
>(({
  data,
  config,
  className,
  height = 300,
  showGrid = true,
  showAxes = true,
  showTooltip = true,
  showLegend = false,
  id,
  children,
  ...props
}, ref) => {
  return (
    <UIBox ref={ref} className={cn("w-full", className)} {...props}>
      <ChartContainer
        id={id}
        config={config}
        className="w-full"
        style={{ minHeight: typeof height === 'number' ? `${height}px` : height, aspectRatio: 'auto' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </ChartContainer>
    </UIBox>
  )
})
UIChart.displayName = "UIChart"

// ============================================================================
// Area Chart Components
// ============================================================================

export interface UIAreaChartProps extends Omit<UIChartProps, 'children'> {
  dataKeys: string[]
  type?: "monotone" | "linear" | "step"
  stacked?: boolean
  fillOpacity?: number
}

export const UIAreaChart = React.forwardRef<
  HTMLDivElement,
  UIAreaChartProps
>(({
  data,
  config,
  dataKeys,
  type = "monotone",
  stacked = false,
  fillOpacity = 0.6,
  showGrid = true,
  showAxes = true,
  showTooltip = true,
  showLegend = false,
  className,
  height = 300,
  id,
  ...props
}, ref) => {
  return (
    <UIChart
      ref={ref}
      data={data}
      config={config}
      className={className}
      height={height}
      id={id}
      {...props}
    >
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
        {showAxes && (
          <>
            <XAxis
              dataKey="name"
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
            <YAxis
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
          </>
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent />}
          />
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}

        {dataKeys.map((key, index) => (
          <Area
            key={key}
            type={type}
            dataKey={key}
            stackId={stacked ? "1" : undefined}
            stroke={`var(--color-${key})`}
            fill={`var(--color-${key})`}
            fillOpacity={fillOpacity}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </UIChart>
  )
})
UIAreaChart.displayName = "UIAreaChart"

// ============================================================================
// Bar Chart Components
// ============================================================================

export interface UIBarChartProps extends Omit<UIChartProps, 'children'> {
  dataKeys: string[]
  orientation?: "horizontal" | "vertical"
  barSize?: number
  radius?: number
}

export const UIBarChart = React.forwardRef<
  HTMLDivElement,
  UIBarChartProps
>(({
  data,
  config,
  dataKeys,
  orientation = "vertical",
  barSize,
  radius = 4,
  showGrid = true,
  showAxes = true,
  showTooltip = true,
  showLegend = false,
  className,
  height = 300,
  id,
  ...props
}, ref) => {
  return (
    <UIChart
      ref={ref}
      data={data}
      config={config}
      className={className}
      height={height}
      id={id}
      {...props}
    >
      <BarChart
        data={data}
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        layout={orientation === "horizontal" ? "horizontal" : "vertical"}
      >
        {showGrid && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
        {showAxes && (
          <>
            <XAxis
              dataKey="name"
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
            <YAxis
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
          </>
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
            content={<ChartTooltipContent />}
          />
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}

        {dataKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={`var(--color-${key})`}
            radius={[radius, radius, 0, 0]}
            maxBarSize={barSize}
          />
        ))}
      </BarChart>
    </UIChart>
  )
})
UIBarChart.displayName = "UIBarChart"

// ============================================================================
// Line Chart Components
// ============================================================================

export interface UILineChartProps extends Omit<UIChartProps, 'children'> {
  dataKeys: string[]
  type?: "monotone" | "linear" | "step"
  strokeWidth?: number
  dot?: boolean
  connectNulls?: boolean
}

export const UILineChart = React.forwardRef<
  HTMLDivElement,
  UILineChartProps
>(({
  data,
  config,
  dataKeys,
  type = "monotone",
  strokeWidth = 2,
  dot = true,
  connectNulls = false,
  showGrid = true,
  showAxes = true,
  showTooltip = true,
  showLegend = false,
  className,
  height = 300,
  id,
  ...props
}, ref) => {
  return (
    <UIChart
      ref={ref}
      data={data}
      config={config}
      className={className}
      height={height}
      id={id}
      {...props}
    >
      <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
        {showAxes && (
          <>
            <XAxis
              dataKey="name"
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
            <YAxis
              className="fill-muted-foreground text-xs"
              tickLine={true}
              axisLine={true}
              stroke="hsl(var(--border))"
            />
          </>
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={{ stroke: 'var(--color-border)', strokeWidth: 1 }}
            content={<ChartTooltipContent />}
          />
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}

        {dataKeys.map((key, index) => (
          <Line
            key={key}
            type={type}
            dataKey={key}
            stroke={`var(--color-${key})`}
            strokeWidth={strokeWidth}
            dot={dot ? { fill: `var(--color-${key})`, strokeWidth: 2, r: 4 } : false}
            activeDot={{ r: 6, stroke: `var(--color-${key})`, strokeWidth: 2 }}
            connectNulls={connectNulls}
          />
        ))}
      </LineChart>
    </UIChart>
  )
})
UILineChart.displayName = "UILineChart"

// ============================================================================
// Pie Chart Components
// ============================================================================

export interface UIPieChartProps extends Omit<UIChartProps, 'children'> {
  dataKey: string
  nameKey?: string
  innerRadius?: number
  outerRadius?: number
  startAngle?: number
  endAngle?: number
  showLabels?: boolean
}

export const UIPieChart = React.forwardRef<
  HTMLDivElement,
  UIPieChartProps
>(({
  data,
  config,
  dataKey,
  nameKey = "name",
  innerRadius = 0,
  outerRadius = 80,
  startAngle = 90,
  endAngle = -270,
  showLabels = false,
  showTooltip = true,
  showLegend = true,
  className,
  height = 300,
  id,
  ...props
}, ref) => {
  // Generate colors for pie slices based on data names and config
  const getColorForSlice = (entry: any, index: number) => {
    const name = entry[nameKey]
    if (config[name]?.color) {
      return `var(--color-${name})`
    }
    // Fallback to index-based chart colors
    const chartKeys = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5']
    return `var(--${chartKeys[index % chartKeys.length]})`
  }

  return (
    <UIChart
      ref={ref}
      data={data}
      config={config}
      className={className}
      height={height}
      id={id}
      {...props}
    >
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          dataKey={dataKey}
          nameKey={nameKey}
          label={showLabels}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={getColorForSlice(entry, index)}
            />
          ))}
        </Pie>
        {showTooltip && (
          <ChartTooltip content={<ChartTooltipContent />} />
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </PieChart>
    </UIChart>
  )
})
UIPieChart.displayName = "UIPieChart"

// ============================================================================
// Donut Chart (Pie with inner radius)
// ============================================================================

export interface UIDonutChartProps extends UIPieChartProps {}

export const UIDonutChart = React.forwardRef<
  HTMLDivElement,
  UIDonutChartProps
>(({ innerRadius = 60, ...props }, ref) => {
  return <UIPieChart ref={ref} innerRadius={innerRadius} {...props} />
})
UIDonutChart.displayName = "UIDonutChart"

// ============================================================================
// Radar Chart Components
// ============================================================================

export interface UIRadarChartProps extends Omit<UIChartProps, 'children'> {
  dataKeys: string[]
  cx?: string | number
  cy?: string | number
  outerRadius?: string | number
  startAngle?: number
  endAngle?: number
  showGrid?: boolean
  showAngleAxis?: boolean
  showRadiusAxis?: boolean
  gridType?: "polygon" | "circle"
  fillOpacity?: number
  strokeWidth?: number
  dot?: boolean
}

export const UIRadarChart = React.forwardRef<
  HTMLDivElement,
  UIRadarChartProps
>((
  {
    data,
    config,
    dataKeys,
    cx = "50%",
    cy = "50%",
    outerRadius = "80%",
    startAngle = 90,
    endAngle = -270,
    showGrid = true,
    showAngleAxis = true,
    showRadiusAxis = false,
    gridType = "polygon",
    fillOpacity = 0.6,
    strokeWidth = 2,
    dot = false,
    showTooltip = true,
    showLegend = false,
    className,
    height = 300,
    id,
    ...props
  },
  ref
) => {
  return (
    <UIChart
      ref={ref}
      data={data}
      config={config}
      className={className}
      height={height}
      id={id}
      {...props}
    >
      <RadarChart
        data={data}
        cx={cx}
        cy={cy}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
      >
        {showGrid && (
          <PolarGrid
            gridType={gridType}
            className="stroke-muted"
          />
        )}
        {showAngleAxis && (
          <PolarAngleAxis
            dataKey="name"
            className="fill-muted-foreground text-xs"
            tickLine={false}
          />
        )}
        {showRadiusAxis && (
          <PolarRadiusAxis
            className="fill-muted-foreground text-xs"
            tickLine={false}
            axisLine={false}
          />
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent />}
          />
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}

        {dataKeys.map((key, index) => (
          <Radar
            key={key}
            name={String(config[key]?.label || key)}
            dataKey={key}
            stroke={`var(--color-${key})`}
            fill={`var(--color-${key})`}
            fillOpacity={fillOpacity}
            strokeWidth={strokeWidth}
            dot={dot}
          />
        ))}
      </RadarChart>
    </UIChart>
  )
})
UIRadarChart.displayName = "UIRadarChart"

// ============================================================================
// Radial Chart Components (RadialBarChart)
// ============================================================================

export interface UIRadialChartProps extends Omit<UIChartProps, 'children'> {
  dataKey: string
  cx?: string | number
  cy?: string | number
  innerRadius?: string | number
  outerRadius?: string | number
  startAngle?: number
  endAngle?: number
  cornerRadius?: number
  label?: boolean
  /**
   * 접근성을 위한 차트 설명
   */
  'aria-label'?: string
  /**
   * 접근성을 위한 차트 제목 (참조용)
   */
  'aria-labelledby'?: string
}

export const UIRadialChart = React.forwardRef<
  HTMLDivElement,
  UIRadialChartProps
>((
  {
    data,
    config,
    dataKey,
    cx = "50%",
    cy = "50%",
    innerRadius = "30%",
    outerRadius = "70%",
    startAngle = 90,
    endAngle = -270,
    cornerRadius = 4,
    label = false,
    showTooltip = true,
    showLegend = false,
    className,
    height = 300,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...props
  },
  ref
) => {
  // 차트 설명을 생성합니다
  const chartDescription = React.useMemo(() => {
    if (ariaLabel) return ariaLabel

    // 데이터 기반 기본 설명 생성
    if (data && data.length > 0) {
      const values = data.map(item => `${item.name || 'Item'}: ${item[dataKey] || 0}%`).join(', ')
      return `Radial chart displaying ${values}`
    }

    return "Radial chart"
  }, [ariaLabel, data, dataKey])

  // SVG 요소들을 접근성에서 숨기는 effect
  React.useEffect(() => {
    if (ref && 'current' in ref && ref.current) {
      // 모든 SVG path 요소에 aria-hidden="true" 추가
      const svgPaths = ref.current.querySelectorAll('svg path[role="img"]')
      svgPaths.forEach(path => {
        path.setAttribute('aria-hidden', 'true')
      })
    }
  }, [ref, data, dataKey])

  return (
    <UIBox
      ref={ref}
      role="img"
      aria-label={chartDescription}
      aria-labelledby={ariaLabelledBy}
      className={cn("w-full", className)}
    >
      <UIChart
        data={data}
        config={config}
        height={height}
        id={id}
        {...props}
      >
        <RadialBarChart
          data={data}
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
        >
          <RadialBar
            dataKey={dataKey}
            cornerRadius={cornerRadius}
            fill="var(--color-primary)"
            label={label}
          />
          {showTooltip && (
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent />}
            />
          )}
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
        </RadialBarChart>
      </UIChart>
    </UIBox>
  )
})
UIRadialChart.displayName = "UIRadialChart"

// ============================================================================
// Type Re-exports
// ============================================================================

export type { ChartConfig } from "@workspace/ui/components/chart"